import React, { useEffect, useMemo, useState } from "react";
import { FiCheck, FiChevronDown, FiChevronRight, FiCopy, FiX } from "react-icons/fi";
import { durationParts, durationText, rulesErrors, rulesForColumns } from "../../utils/historicalModelRuns";
import type { ColumnRules, DurationUnit, RuleError, RulesByColumn } from "../../utils/historicalModelRuns";
import type { FieldDefinition } from "../../types/modelSchemas";

interface RulesFieldProps {
  fieldKey: string;
  field: Pick<FieldDefinition, "description" | "defaultValue"> | null | undefined;
  value: unknown;
  onChange: (value: RulesByColumn) => void;
  error?: string;
  /** The columns ticked in the dataset field the rules describe (`validation.rulesFor`) */
  columns: string[];
}

const GAP_UNITS: DurationUnit[] = ["minutes", "hours", "days"];

const inputClass = "w-24 rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-eai-primary-accent";

// A number being typed: "-", "-1", ".", "2." are on the way to a number and stay in the box without a store write
const NUMBER_PREFIX = /^-?\d*\.?\d*$/;

const NumberBox: React.FC<{ value: number | undefined; onChange: (n: number | undefined) => void; label: string; testId: string }> = ({ value, onChange, label, testId }) => {
  const [text, setText] = useState(value === undefined ? "" : String(value));
  // Follow an outside change (a reset, another box) but never overwrite what is being typed for the same number
  useEffect(() => {
    setText((current) => {
      if (value === undefined) return current.trim() === "" || Number.isNaN(Number(current)) ? current : "";
      return Number(current) === value ? current : String(value);
    });
  }, [value]);
  return (
    <label className="flex items-center gap-1 text-xs text-gray-600">
      {label}
      <input
        type="text"
        inputMode="decimal"
        value={text}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          if (next.trim() === "") {
            onChange(undefined);
            return;
          }
          const n = Number(next);
          // A partial number ("-", "1.") or a letter stays in the box and leaves the stored value alone
          if (!NUMBER_PREFIX.test(next.trim()) || Number.isNaN(n)) return;
          onChange(n);
        }}
        className={inputClass}
        data-testid={testId}
      />
    </label>
  );
};

/** One column's rules: sentinel values, below minimum, clamp, fill gaps. */
const ColumnCard: React.FC<{
  column: string;
  rules: ColumnRules;
  errors: RuleError[];
  onChange: (rules: ColumnRules) => void;
}> = ({ column, rules, errors, onChange }) => {
  const [sentinelText, setSentinelText] = useState("");
  const gap = durationParts(rules.fillGaps?.maxGap);
  const [gapUnit, setGapUnit] = useState<DurationUnit>(gap?.unit ?? "hours");
  const [gapValue, setGapValue] = useState<string>(gap ? String(gap.value) : "");
  const [gapRaw, setGapRaw] = useState<string | null>(null);

  const set = (patch: Partial<ColumnRules>) => onChange({ ...rules, ...patch });
  const drop = (key: keyof ColumnRules) => {
    const next = { ...rules };
    delete next[key];
    onChange(next);
  };
  const errorFor = (key: string) => errors.find((e) => e.key === key)?.message;

  const addSentinel = () => {
    const n = Number(sentinelText);
    if (sentinelText.trim() === "" || Number.isNaN(n)) {
      // Keep the bad text so the error shows; the processor only takes numbers
      set({ sentinels: [...(rules.sentinels ?? []), sentinelText as unknown as number] });
      return;
    }
    set({ sentinels: Array.from(new Set([...(rules.sentinels ?? []), n])) });
    setSentinelText("");
  };

  const updateGap = (valueText: string, unit: DurationUnit) => {
    setGapValue(valueText);
    setGapUnit(unit);
    const n = Number(valueText);
    if (valueText.trim() === "" || Number.isNaN(n) || n <= 0) {
      setGapRaw(valueText);
      set({ fillGaps: { maxGap: valueText } });
      return;
    }
    setGapRaw(null);
    set({ fillGaps: { maxGap: durationText(n, unit) } });
  };

  return (
    <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-3" data-testid={`rules-${column}`}>
      <div className="text-sm font-medium text-gray-800">{column}</div>

      <div className="space-y-1">
        <span className="block text-xs font-medium text-gray-700">Sentinel values (removed)</span>
        <div className="flex flex-wrap items-center gap-1.5">
          {(rules.sentinels ?? []).map((s, i) => (
            <span key={`${s}-${i}`} className="inline-flex items-center gap-1 rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs" data-testid="sentinel-chip">
              {String(s)}
              <button type="button" onClick={() => set({ sentinels: (rules.sentinels ?? []).filter((_, j) => j !== i) })} aria-label={`Remove ${s}`} className="text-gray-500 hover:text-gray-800">
                <FiX size={11} />
              </button>
            </span>
          ))}
          <input
            type="text"
            inputMode="decimal"
            value={sentinelText}
            onChange={(e) => setSentinelText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addSentinel();
              }
            }}
            placeholder="-9999"
            className={inputClass}
            data-testid="sentinel-input"
          />
          <button type="button" onClick={addSentinel} className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-100" data-testid="sentinel-add">
            Add
          </button>
        </div>
        {errorFor("sentinels") && <p className="text-xs text-red-600">{errorFor("sentinels")}</p>}
      </div>

      <div className="space-y-1">
        <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
          <input type="checkbox" checked={Boolean(rules.belowMin)} onChange={(e) => (e.target.checked ? set({ belowMin: { min: 0, set: 0 } }) : drop("belowMin"))} data-testid="belowMin-on" />
          Below minimum
        </label>
        {rules.belowMin && (
          <div className="flex flex-wrap gap-3 pl-5">
            <NumberBox label="Min" value={rules.belowMin.min} onChange={(n) => set({ belowMin: { ...(rules.belowMin as any), min: n as number } })} testId="belowMin-min" />
            <NumberBox label="Replace with" value={rules.belowMin.set} onChange={(n) => set({ belowMin: { ...(rules.belowMin as any), set: n as number } })} testId="belowMin-set" />
          </div>
        )}
        {errorFor("belowMin") && <p className="text-xs text-red-600">{errorFor("belowMin")}</p>}
      </div>

      <div className="space-y-1">
        <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
          <input type="checkbox" checked={Boolean(rules.clamp)} onChange={(e) => (e.target.checked ? set({ clamp: { min: 0, max: 0 } }) : drop("clamp"))} data-testid="clamp-on" />
          Clamp
        </label>
        {rules.clamp && (
          <div className="flex flex-wrap gap-3 pl-5">
            <NumberBox label="Min" value={rules.clamp.min} onChange={(n) => set({ clamp: { ...(rules.clamp as any), min: n as number } })} testId="clamp-min" />
            <NumberBox label="Max" value={rules.clamp.max} onChange={(n) => set({ clamp: { ...(rules.clamp as any), max: n as number } })} testId="clamp-max" />
          </div>
        )}
        {errorFor("clamp") && <p className="text-xs text-red-600">{errorFor("clamp")}</p>}
      </div>

      <div className="space-y-1">
        <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
          <input
            type="checkbox"
            checked={Boolean(rules.fillGaps)}
            onChange={(e) => {
              if (e.target.checked) {
                setGapValue("6");
                setGapUnit("hours");
                set({ fillGaps: { maxGap: "PT6H" } });
              } else drop("fillGaps");
            }}
            data-testid="fillGaps-on"
          />
          Fill gaps
        </label>
        {rules.fillGaps && (
          <div className="flex flex-wrap items-center gap-2 pl-5 text-xs text-gray-600">
            Longest gap to fill
            <input type="text" inputMode="numeric" value={gapRaw ?? gapValue} onChange={(e) => updateGap(e.target.value, gapUnit)} className={inputClass} data-testid="fillGaps-value" />
            <select value={gapUnit} onChange={(e) => updateGap(gapRaw ?? gapValue, e.target.value as DurationUnit)} className="rounded-md border border-gray-300 px-2 py-1 text-sm" data-testid="fillGaps-unit">
              {GAP_UNITS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>
        )}
        {errorFor("fillGaps") && <p className="text-xs text-red-600">{errorFor("fillGaps")}</p>}
      </div>
    </div>
  );
};

/**
 * The per-column rules form (stage 4, decision 5) for a `dict` field whose template says `validation.rulesFor`:
 * one card per ticked column, prefilled from the template's defaults, checked as typed; under it a read-only JSON
 * view of what will be saved, for developers.
 */
const RulesField: React.FC<RulesFieldProps> = ({ fieldKey, field, value, onChange, error, columns }) => {
  const rules = useMemo(() => rulesForColumns(value, columns, field?.defaultValue), [value, columns, field?.defaultValue]);
  const [showJson, setShowJson] = useState(false);
  const [copied, setCopied] = useState(false);

  // Follow the ticked columns: a card per column, new ones from the defaults, un-ticked ones dropped
  const synced = JSON.stringify(rules);
  const current = JSON.stringify(value ?? null);
  useEffect(() => {
    if (synced !== current) onChange(rules);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [synced]);

  const errors = useMemo(() => rulesErrors(rules, columns), [rules, columns]);
  const json = useMemo(() => JSON.stringify(rules, null, 2), [rules]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="space-y-3" data-testid={`rules-field-${fieldKey}`}>
      {field?.description && <p className="text-xs text-gray-500">{field.description}</p>}
      {columns.length === 0 ? (
        <p className="text-xs text-gray-500">Tick the dataset's columns above to set their rules.</p>
      ) : (
        columns.map((column) => (
          <ColumnCard key={column} column={column} rules={rules[column] ?? {}} errors={errors.filter((e) => e.column === column)} onChange={(next) => onChange({ ...rules, [column]: next })} />
        ))
      )}
      {errors
        .filter((e) => !e.key)
        .map((e) => (
          <p key={e.message} className="text-sm text-red-600">
            {e.message}
          </p>
        ))}
      {error && errors.length === 0 && <p className="text-sm text-red-600">{error}</p>}

      <div className="rounded-md border border-dashed border-gray-300">
        <button type="button" onClick={() => setShowJson((v) => !v)} aria-expanded={showJson} className="flex w-full items-center gap-1 px-3 py-2 text-left text-xs text-gray-600 hover:bg-gray-50" data-testid="rules-json-toggle">
          {showJson ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
          Rules as JSON (for development purposes)
        </button>
        {showJson && (
          <div className="space-y-2 border-t border-dashed border-gray-300 p-3" data-testid="rules-json">
            <p className="text-xs text-gray-500">For development purposes. Edit the rules above; this view shows what is saved for the processor.</p>
            <div className="relative">
              <pre className="max-h-64 overflow-auto rounded-md bg-gray-900 p-3 font-mono text-xs text-gray-100" data-testid="rules-json-text">
                {json}
              </pre>
              <button type="button" onClick={copy} className="absolute right-2 top-2 inline-flex items-center gap-1 rounded border border-gray-500 bg-gray-800 px-2 py-0.5 text-xs text-gray-100 hover:bg-gray-700" aria-label="Copy JSON">
                {copied ? <FiCheck size={11} /> : <FiCopy size={11} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RulesField;
